import { ArrowUpTrayIcon, CheckCircleIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useId, useMemo, useState } from 'react'

/**
 * Dropzone
 * Props:
 * - files: Array<File | { name?: string, originalName?: string, fileName?: string }>
 * - onFiles(files: File[]) => void
 * - onRemove?(index: number) => void
 * - accept?: string
 * - multiple?: boolean
 * - label?: string
 * - helper?: string
 */
export function Dropzone({
  files = [],
  onFiles,
  onRemove,
  accept = '',
  multiple = true,
  label = 'Klik om te uploaden',
  helper = '',
  className = '',
}) {
  const reactId = useId()
  const inputId = useMemo(() => `dropzone-${reactId}`, [reactId])
  const [isDragging, setIsDragging] = useState(false)
  const [rejectedCount, setRejectedCount] = useState(0)

  const normalized = useMemo(() => {
    return (files || []).map((f) => {
      const name = f?.name || f?.originalName || f?.fileName || 'Bestand'
      const size = typeof f?.size === 'number' ? f.size : null // only for File objects
      return { raw: f, name, size }
    })
  }, [files])

  const hasFiles = normalized.length > 0

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || [])
    if (picked.length) onFiles?.(picked)
    e.target.value = ''
  }

  const onDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const onDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const matchesAccept = (file, accept) => {
    if (!accept) return true
    return accept
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .some((type) => {
        if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type)
        if (type.endsWith('/*')) return file.type.startsWith(type.slice(0, -1))
        return file.type === type
      })
  }

  const onDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const all = Array.from(e.dataTransfer.files || [])
    const accepted = all.filter((f) => matchesAccept(f, accept))
    const rejected = all.length - accepted.length
    setRejectedCount(rejected)
    if (accepted.length) onFiles?.(accepted)
  }

  const formatSize = (bytes) => {
    if (bytes == null) return null
    const kb = bytes / 1024
    if (kb < 1024) return `${Math.round(kb)} KB`
    const mb = kb / 1024
    return `${mb.toFixed(1)} MB`
  }

  return (
    <div className={className}>
      {/* Drop area */}
      <div
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          'rounded-xl border border-dashed px-6 py-8 transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-primary/60 bg-white',
        ].join(' ')}
      >
        <div className="flex items-center justify-center gap-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white">
            <ArrowUpTrayIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          </span>

          <div className="text-center">
            <div className="text-sm text-gray-600">
              <label
                htmlFor={inputId}
                className="cursor-pointer font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:text-primary-600"
              >
                {label}
                <input
                  id={inputId}
                  type="file"
                  className="sr-only"
                  multiple={multiple}
                  accept={accept}
                  onChange={pickFiles}
                />
              </label>
              <span className="text-gray-500"> of sleep hier naartoe</span>
            </div>

            {helper ? <div className="mt-2 text-xs text-gray-400">{helper}</div> : null}
          </div>
        </div>
      </div>

      {/* File list BELOW */}
      {hasFiles ? (
        <div className="mt-4">
          {multiple && normalized.length > 1 ? (
            // Multiple files: show single summary row
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50">
                  <DocumentIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900">
                    {normalized.length} bestanden geselecteerd
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-green-600">
                    <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                    100%
                  </div>
                </div>
              </div>
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => normalized.forEach((_, idx) => onRemove(idx))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                  aria-label="Verwijder alle bestanden"
                >
                  <TrashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : (
            // Single file
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50">
                  <DocumentIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900">{normalized[0].name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                    {normalized[0].size != null ? <span>{formatSize(normalized[0].size)}</span> : null}
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                      100%
                    </span>
                  </div>
                </div>
              </div>
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(0)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                  aria-label={`Verwijder ${normalized[0].name}`}
                >
                  <TrashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
      {rejectedCount > 0 && (
        <p className="mt-2 text-xs text-red-600">
          {rejectedCount} bestand{rejectedCount > 1 ? 'en' : ''} geweigerd. Alleen {helper} bestanden zijn toegestaan.
        </p>
      )}
    </div>
  )
}
